import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NodeExpression, FieldExpression, term, toString as luceneToString } from 'lucene';
import { firstValueFrom, of, tap } from 'rxjs';
import { Router, ActivatedRoute } from '@angular/router';

import { ORDER_SERVER_URL } from '../../config';
import { PaginatedResult } from '../../shared/models/paginated-result';
import { OrderBy } from '../../shared/utils/constant';
import { FavoriteOrders } from '../models/favorite-orders';
import {
	FieldOfScheduleBusinessOrder,
	ScheduleBusinessOrder,
	ScheduleBusinessOrderNeeds
} from '../models/schedule-business-order';
import { ScheduleBusinessOrderBatch } from '../models/schedule-business-order-batch';
import { FieldOfScheduleBusinessOrderAggregate, ScheduleBusinessOrderAggregate }
	from '../models/aggregation/schedule-business-order-aggregate';
import { FieldOfSBOrderChar,
	ScheduleBusinessOrderCharacteristic } from '../models/schedule-business-order-characteristic';
import { characteristicFields } from '../models/order';
import { UserDatacenterView } from '../../shared/models/datacenter-aggrid-view';
import { DatacenterFilterType } from '../../shared/models/datacenter-filters';
import { Document } from '../../commercial-documents/models/document';

const GET_SCHEDULE_BUSINESS_ORDER_URL = `${ ORDER_SERVER_URL }/v3/scheduleBusinessOrders`;
const GET_FAVORITE_ORDERS_URL = `${ ORDER_SERVER_URL }/v3/userFavoriteOrders`;
const GET_SCHEDULE_BUSINESS_NEEDS_URL = `${ ORDER_SERVER_URL }/v3/scheduleBusinessOrder`;
const SB_DATACENTER_ROUTE_LINK = '/order-tracking/schedule-business-order/datacenter';
export const ORDER_REDIRECT_URL = '/order-tracking/order/datacenter';

@Injectable({ providedIn: 'root' })
export class ScheduleBusinessOrderService {
	private scheduleBusinessOrderNeeds?: PaginatedResult<ScheduleBusinessOrderNeeds>;
	private readonly previousState: Record<string, unknown> | undefined;

	constructor(
		private readonly http: HttpClient,
		private readonly router: Router,
		private readonly activedRoute: ActivatedRoute,
	) {}

	getScheduleBusinessOrders(
		fields: readonly FieldOfScheduleBusinessOrder[],
		filter: string | undefined,
		index: number,
		limit: number,
		orderBy: OrderBy<keyof ScheduleBusinessOrder>[] | OrderBy<keyof ScheduleBusinessOrder>,
		onlyDistinct?: boolean
	) {
		const url = new URL(GET_SCHEDULE_BUSINESS_ORDER_URL);
		const params = url.searchParams;
		params.append('fields', fields.join(','));
		if (filter) {
			params.append('filter', filter);
		}
		params.append('page', String(Math.floor(index / limit) + 1));
		params.append('limit', String(limit));

		params.append('orderBy', Array.isArray(orderBy) ? orderBy.join(',') : String(orderBy));

		params.append('distinct', String(onlyDistinct));

		return this.http.get<PaginatedResult<ScheduleBusinessOrder>>(url.toString());
	}

	getFavoriteScheduleBusinessOrders() {
		const url = new URL(GET_FAVORITE_ORDERS_URL);

		return this.http.get<FavoriteOrders[]>(url.toString());
	}

	saveScheduleBusinessOrders(data: FavoriteOrders) {
		const url = new URL(`${ GET_FAVORITE_ORDERS_URL }:save`);

		return this.http.post<FavoriteOrders>(url.toString(), data);
	}

	deleteScheduleBusinessOrders(favoriteOrderId: number) {
		const url = new URL(`${ GET_FAVORITE_ORDERS_URL }:delete`);

		return this.http.post<''>(url.toString(), favoriteOrderId);
	}

	getScheduleBusinessOrderCount(textSearch: string) {
		const url = new URL(GET_SCHEDULE_BUSINESS_ORDER_URL);
		const params = url.searchParams;
		if (textSearch !== '') {
			const filter = this.createLuceneFilter(textSearch);
			params.append('filter', filter);
		}
		params.append('onlyCount', 'true');

		return this.http.get<PaginatedResult<ScheduleBusinessOrder>>(url.toString());
	}

	createLuceneFilter(textSearch: string) {
		const search = `*${ term.escape(textSearch) }*`;

		const customerArticleReferenceField: FieldExpression = {
			field: 'customerArticleReference',
			term: search,
			quoted: false,
		};
		const steelgradeField: FieldExpression = {
			field: 'steelgradeDescription',
			term: search,
			quoted: false
		};
		const customerOrderNumberField: FieldExpression = {
			field: 'customerOrderNumber',
			term: search,
			quoted: false
		};
		const supplierOrderNumberField: FieldExpression = {
			field: 'supplierOrderNumber',
			term: search,
			quoted: false
		};

		const steelgradeCustomerNode: NodeExpression = {
			left: steelgradeField,
			operator: 'OR',
			parenthesized: false,
			right: customerOrderNumberField
		};
		const customerArticleReferenceSupplierNode: NodeExpression = {
			left: customerArticleReferenceField,
			operator: 'OR',
			parenthesized: false,
			right: supplierOrderNumberField
		};

		const allNode: NodeExpression = {
			left: steelgradeCustomerNode,
			operator: 'OR',
			parenthesized: true,
			right: customerArticleReferenceSupplierNode
		};
		return luceneToString(allNode);
	}

	getScheduleBusinessOrderCard(
		supplierOrderNumber: string,
		fields: readonly FieldOfScheduleBusinessOrder[] | undefined,
	) {
		const sbOrderId = encodeURIComponent(supplierOrderNumber);
		const url = new URL(`${ GET_SCHEDULE_BUSINESS_ORDER_URL }/${ sbOrderId }`);
		const params = url.searchParams;
		if (fields) {
			params.append('fields', fields.join(','));
		}
		return this.http.get<ScheduleBusinessOrder>(url.toString());
	}

	getSBOrderCharacteritics(
		supplierReference: string
	) {
		const orderId = encodeURIComponent(supplierReference);
		const url = new URL(`${ GET_SCHEDULE_BUSINESS_ORDER_URL }/${ orderId }/characteristics`);
		const params = url.searchParams;
		const fields: readonly FieldOfSBOrderChar[] = characteristicFields;
		params.append('fields', fields.join(','));
		return this.http.get<PaginatedResult<ScheduleBusinessOrderCharacteristic>>(url.toString());
	}

	getScheduleBusinessNeeds(supplierOrderNumber: string) {
		if (
			this.scheduleBusinessOrderNeeds && this.scheduleBusinessOrderNeeds.count > 0
			&& this.scheduleBusinessOrderNeeds.results[0].supplierOrderNumber === supplierOrderNumber
		) {
			return of(this.scheduleBusinessOrderNeeds);
		}
		const sbOrderId = encodeURIComponent(supplierOrderNumber);
		const url = new URL(`${ GET_SCHEDULE_BUSINESS_NEEDS_URL }/${ sbOrderId }/needs`);
		return this.http.get<PaginatedResult<ScheduleBusinessOrderNeeds>>(url.toString()).pipe(
			tap(scheduleBusinessOrderNeeds => {
				this.scheduleBusinessOrderNeeds = scheduleBusinessOrderNeeds;
			})
		);
	}

	getBatchesByScheduleBusinessOrderKey(
		supplierOrderNumber: string,
		filter: string | undefined,
		limit: number | undefined,
	) {
		const sbOrderId = encodeURIComponent(supplierOrderNumber);
		const url = new URL(`${ GET_SCHEDULE_BUSINESS_ORDER_URL }/${ sbOrderId }/batches`);
		const params = url.searchParams;
		if (limit) {
			params.append('limit', String(limit));
		}
		if (filter) {
			params.append('filter', filter);
		}
		return this.http.get<PaginatedResult<ScheduleBusinessOrderBatch>>(url.toString());
	}

	getTotalQuantity(
		supplierOrderNumber: string,
		filter: string,
		distinct: boolean,
		fields: string[],
		orderBy?: string
	) {
		const sbOrderId = encodeURIComponent(supplierOrderNumber);
		const url = new URL(`${ GET_SCHEDULE_BUSINESS_ORDER_URL }/${ sbOrderId }/batches`);
		const params = url.searchParams;
		if (filter) {
			params.append('filter', filter);
		}
		if (distinct) {
			params.append('distinct', 'true');
		}
		if (orderBy) {
			params.append('orderBy', orderBy);
		}
		params.append('fields', fields.join(','));
		return this.http.get<PaginatedResult<ScheduleBusinessOrderBatch>>(url.toString());
	}

	createQuickSerachFilter(textSearch: string) {
		const search = `*${ term.escape(textSearch) }*`;

		const customerOrderNumberField: FieldExpression = {
			field: 'customerOrderNumber',
			term: search,
			quoted: false
		};
		const supplierOrderNumberField: FieldExpression = {
			field: 'supplierOrderNumber',
			term: search,
			quoted: false
		};
		const supplierOrderNumberCustomField: NodeExpression = {
			left: customerOrderNumberField,
			operator: 'OR',
			parenthesized: false,
			right: supplierOrderNumberField
		};
		const customerArticleReferenceField: FieldExpression = {
			field: 'customerArticleReference',
			term: search,
			quoted: false,
		};

		const allNode: NodeExpression = {
			left: supplierOrderNumberCustomField,
			operator: 'OR',
			parenthesized: true,
			right: customerArticleReferenceField
		};
		return luceneToString(allNode);
	}

	getScheduleBusinessOrderAggregateQuantity(
		fields: readonly FieldOfScheduleBusinessOrderAggregate[],
		filter?: string
	) {
		const url = new URL(`${ GET_SCHEDULE_BUSINESS_ORDER_URL }:aggregateQuantity`);
		const params = url.searchParams;

		params.append('fields', fields.join(','));

		if (filter) {
			params.append('filter', filter);
		}

		return this.http.get<ScheduleBusinessOrderAggregate[]>(url.toString());
	}

	async navigation(defaultView: UserDatacenterView, routePath: string) {
		await this.router.navigate([routePath], {
			relativeTo: this.activedRoute,
			state: { ...this.previousState, view: defaultView }
		});
	}

	async sbOrderDatacenterFilter(document: Document, defaultView: UserDatacenterView) {
		const filtersList: DatacenterFilterType[] = [];
		const orderNumberValues = [];
		if (document.type === 'IN' || document.type === 'DA') {
			for (const documentLine of document.documentLines) {
				orderNumberValues.push(
					documentLine.supplierOrderReference
				);
			}
		} else {
			orderNumberValues.push(
				document.supplierOrderReference
			);
		}
		const orderFilter: DatacenterFilterType = {
			id: 'supplierOrderNumber',
			values: orderNumberValues
		};
		filtersList.push(orderFilter);
		defaultView.filterState = filtersList;
		await this.navigation(defaultView, SB_DATACENTER_ROUTE_LINK);
	}

	setRedirectionLink(
		document: Document,
		scheduleBusinessOrderRequiredFields: readonly (keyof ScheduleBusinessOrder)[] | undefined
	) {
		const supplierOrderNumber = document.type === 'IN' || document.type === 'DA'
			? document.documentLines[0].supplierOrderReference
			: document.supplierOrderReference;
		firstValueFrom(this.getScheduleBusinessOrderCard(
			supplierOrderNumber,
			scheduleBusinessOrderRequiredFields
		))
			.then(res => {
				document.isSbOrder = res.supplierOrderType === 'E' || res.supplierOrderType === 'F';
				document.docNavigationLink = document.isSbOrder
					? SB_DATACENTER_ROUTE_LINK
					: ORDER_REDIRECT_URL;
			})
			.catch((error: unknown) => {
				document.isSbOrder = false;
				document.docNavigationLink = ORDER_REDIRECT_URL;
				console.error(error);
			});
	}
}
